const LOCK_PREFIX = 'nettgrav:mls:key-package-publish:';
const RESULT_PREFIX = 'nettgrav:mls:key-package-publish-result:';
const LEASE_PREFIX = 'nettgrav:mls:key-package-publish-lease:';

const RESULT_TTL_MS = 15_000;
const LEASE_TTL_MS = 8_000;
const LEASE_WAIT_TIMEOUT_MS = 30_000;
const FAILURE_BACKOFF_BASE_MS = 1_000;
const FAILURE_BACKOFF_MAX_MS = 30_000;
const FAILURE_WINDOW_MS = 5 * 60_000;

const wait = (delayMs) => new Promise((resolve) => setTimeout(resolve, delayMs));

const storage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage || null : null;
  } catch {
    return null;
  }
};

const readJson = (key) => {
  try {
    const value = storage()?.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    storage()?.setItem(key, JSON.stringify(value));
  } catch {}
};

const removeOwnedLease = (key, token) => {
  try {
    const targetStorage = storage();
    const current = readJson(key);
    if (targetStorage && current?.token === token) {
      targetStorage.removeItem(key);
    }
  } catch {}
};

const createToken = () => {
  try {
    return window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  } catch {
    return `${Date.now()}-${Math.random()}`;
  }
};

const normalizeKeyPart = (value, fallback) => {
  const normalized = value == null ? '' : String(value).trim();
  return encodeURIComponent(normalized || fallback);
};

const buildKey = ({ userId, identityId }) =>
  `${normalizeKeyPart(userId, 'anonymous')}:${normalizeKeyPart(identityId, 'self')}`;

const sharedResult = (result) => ({
  ok: Boolean(result?.ok),
  skipped: Boolean(result?.skipped),
  alreadyExists: Boolean(result?.alreadyExists),
  deferred: Boolean(result?.deferred),
  locked: Boolean(result?.locked),
  reason: result?.reason ?? null,
  identityId: result?.identityId ?? null,
  clientId: result?.clientId ?? null,
  ciphersuite: result?.ciphersuite ?? null,
  targetRef: result?.targetRef ?? result?.cacheEntry?.ref ?? null,
});

export class KeyPackagePublishCoordinator {
  constructor() {
    this.inflight = new Map();
  }

  // Forced recovery publishes must bypass a just-completed result; otherwise
  // same-millisecond retries can receive the incomplete result they are repairing.
  run({ userId, identityId, task, reuseRecentResult = true }) {
    if (typeof task !== 'function') {
      throw new TypeError('Key package publish task is required');
    }

    const key = buildKey({ userId, identityId });
    const existing = this.inflight.get(key);
    if (existing) return existing;

    const requestedAt = Date.now();
    const pending = this.#runExclusive({ key, requestedAt, task, reuseRecentResult });
    this.inflight.set(key, pending);

    return pending.finally(() => {
      if (this.inflight.get(key) === pending) {
        this.inflight.delete(key);
      }
    });
  }

  async #runExclusive({ key, requestedAt, task, reuseRecentResult }) {
    const lockManager = typeof navigator !== 'undefined' ? navigator.locks : null;
    if (lockManager?.request) {
      return lockManager.request(`${LOCK_PREFIX}${key}`, { mode: 'exclusive' }, () =>
        this.#runOwned({ key, requestedAt, task, reuseRecentResult }),
      );
    }

    if (!storage()) {
      return this.#runOwned({ key, requestedAt, task, reuseRecentResult });
    }

    return this.#runWithLease({ key, requestedAt, task, reuseRecentResult });
  }

  async #runOwned({ key, requestedAt, task, reuseRecentResult }) {
    const resultKey = `${RESULT_PREFIX}${key}`;
    const recent = readJson(resultKey);
    const completedAt = Number(recent?.completedAt || 0);

    if (
      reuseRecentResult &&
      recent?.result &&
      completedAt >= requestedAt &&
      Date.now() - completedAt <= RESULT_TTL_MS
    ) {
      return {
        ...recent.result,
        skipped: true,
        reason: 'coordinated_publish',
        coordinated: true,
      };
    }

    const retryAt = Number(recent?.failure?.nextAttemptAt || 0);
    if (retryAt > Date.now()) {
      await wait(retryAt - Date.now());
    }

    try {
      const result = await task();
      writeJson(resultKey, {
        completedAt: Date.now(),
        result: sharedResult(result),
      });
      return result;
    } catch (error) {
      const previousFailureAt = Number(recent?.failure?.failedAt || 0);
      const previousAttempts =
        Date.now() - previousFailureAt <= FAILURE_WINDOW_MS
          ? Number(recent?.failure?.attempts || 0)
          : 0;
      const attempts = previousAttempts + 1;
      const backoffMs = Math.min(
        FAILURE_BACKOFF_BASE_MS * 2 ** (attempts - 1),
        FAILURE_BACKOFF_MAX_MS,
      );
      const failedAt = Date.now();
      writeJson(resultKey, {
        failure: {
          attempts,
          failedAt,
          nextAttemptAt: failedAt + backoffMs,
          code: error?.code ?? null,
          status: error?.status ?? null,
        },
      });
      throw error;
    }
  }

  async #runWithLease({ key, requestedAt, task, reuseRecentResult }) {
    const leaseKey = `${LEASE_PREFIX}${key}`;
    const token = createToken();
    const deadline = Date.now() + LEASE_WAIT_TIMEOUT_MS;

    while (Date.now() < deadline) {
      const now = Date.now();
      const current = readJson(leaseKey);
      if (!current?.token || Number(current.expiresAt || 0) <= now) {
        writeJson(leaseKey, { token, expiresAt: now + LEASE_TTL_MS });
        if (readJson(leaseKey)?.token === token) {
          const heartbeat = setInterval(
            () => {
              const active = readJson(leaseKey);
              if (active?.token === token) {
                writeJson(leaseKey, { token, expiresAt: Date.now() + LEASE_TTL_MS });
              }
            },
            Math.floor(LEASE_TTL_MS / 3),
          );

          try {
            return await this.#runOwned({ key, requestedAt, task, reuseRecentResult });
          } finally {
            clearInterval(heartbeat);
            removeOwnedLease(leaseKey, token);
          }
        }
      }

      await wait(50 + Math.floor(Math.random() * 50));
    }

    const error = new Error('Timed out waiting for the MLS key package publish owner');
    error.code = 'MLS_KEY_PACKAGE_PUBLISH_LOCK_TIMEOUT';
    throw error;
  }
}

export const keyPackagePublishCoordinator = new KeyPackagePublishCoordinator();
